import type { MessageSentimentCondition as MessageSentimentConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { ConversationResolutionSentiment } from "@redotech/redo-model/conversation-insights";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

const sentimentDisplayNames: Record<ConversationResolutionSentiment, string> = {
  [ConversationResolutionSentiment.POSITIVE]: "Positive",
  [ConversationResolutionSentiment.NEUTRAL]: "Neutral",
  [ConversationResolutionSentiment.NEGATIVE]: "Negative",
};

const sentimentOptions = Object.values(ConversationResolutionSentiment);

export const MessageSentimentCondition = memo(
  function MessageSentimentCondition({
    condition,
    onChange,
  }: {
    condition: MessageSentimentConditionType;
    onChange: (updated: MessageSentimentConditionType) => void;
  }) {
    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const sentimentItems: RedoListItem<ConversationResolutionSentiment>[] =
      useMemo(() => {
        return sentimentOptions.map((sentiment) => ({
          id: sentiment,
          type: "text" as const,
          text: sentimentDisplayNames[sentiment],
          value: sentiment,
        }));
      }, []);

    const selectedItems = useMemo(() => {
      return (condition.sentiments || []).map((sentiment) => ({
        id: sentiment,
        type: "text" as const,
        text: sentimentDisplayNames[sentiment],
        value: sentiment,
      }));
    }, [condition.sentiments]);

    const displayText =
      (condition.sentiments?.length || 0) > 0
        ? condition.sentiments!.length === 1
          ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            sentimentDisplayNames[condition.sentiments[0]]
          : `${condition.sentiments!.length} sentiments selected`
        : "Select sentiments";

    return (
      <Flex dir="column" gap="sm" w="full">
        <Text color="secondary" fontSize="xs">
          Sentiment
        </Text>
        <RedoFilterDropdownAnchor
          className={styles.fullWidthButton}
          color="primary"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={setDropdownButtonRef}
          text={displayText}
        />
        <RedoMultiselectDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={sentimentItems}
          selectedItems={selectedItems}
          selectionVariant="checkbox"
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) => {
            onChange({
              ...condition,
              sentiments: items.map((item) => item.value),
            });
          }}
          size="xs"
        />
      </Flex>
    );
  },
);
